import { InjectionToken } from "@angular/core";
import { NgModule } from "@angular/core";

declare const process: { env: Partial<EnvironmentVariables> };
export type envTypes = "dev" | "prod" | "test" | "kih";

export class EnvironmentVariables {
  ENV: envTypes; // Is the app running in development or production mode
  VERSION_NUMBER: string; // Provided by our webpack config, not taken from .env
  BUILD_TIMESTAMP: string; // Provided by our webpack config, not taken from .env
  API_URL: string; // The URL for the backend API
  AD_URL_PATH: string; // The URL path for AD login
  ENABLE_DEBUGGING: "YES" | "NO"; // Are quick login etc. available?
  ENABLE_SENTRY: "YES" | "NO"; // Are error messages sent to Sentry?
  QUICK_LOGIN_USER_NAME: string;
  QUICK_LOGIN_PASSWORD: string;
  NEMID_PROD_SERVER_BASE_URL: string;
  NEMID_TEST_SERVER_BASE_URL: string;
  DEFAULT_LANGUAGE: "en-GB" | "da-DK";
  DEV_APP_IP?: string;
  DEV_ADMIN_USER_NAME: string;
  DEV_ADMIN_PASSWORD: string;
  ENABLE_USER_NAME_PASSWORD_FOR_PATIENT_CREATION: "YES" | "NO"; // Are quick login etc. available?
  ENABLE_LANGUAGE_CHANGE: "YES" | "NO";
  EVENT_LOGGING: "YES" | "NO";
  constructor(defaults: Partial<EnvironmentVariables>) {
    this.VERSION_NUMBER = process.env.VERSION_NUMBER;
    this.BUILD_TIMESTAMP = process.env.BUILD_TIMESTAMP;
    this.ENV = defaults.ENV || process.env.ENV;
    this.API_URL = process.env.API_URL || defaults.API_URL;
    this.AD_URL_PATH = process.env.AD_URL_PATH || defaults.AD_URL_PATH;
    this.ENABLE_DEBUGGING =
      process.env.ENABLE_DEBUGGING || defaults.ENABLE_DEBUGGING;
    this.ENABLE_SENTRY = process.env.ENABLE_SENTRY || defaults.ENABLE_SENTRY;
    this.QUICK_LOGIN_USER_NAME =
      process.env.QUICK_LOGIN_USER_NAME || defaults.QUICK_LOGIN_USER_NAME;
    this.QUICK_LOGIN_PASSWORD =
      process.env.QUICK_LOGIN_PASSWORD || defaults.QUICK_LOGIN_PASSWORD;
    this.DEFAULT_LANGUAGE =
      process.env.DEFAULT_LANGUAGE || defaults.DEFAULT_LANGUAGE;
    this.NEMID_PROD_SERVER_BASE_URL =
      process.env.NEMID_PROD_SERVER_BASE_URL ||
      defaults.NEMID_PROD_SERVER_BASE_URL;
    this.NEMID_TEST_SERVER_BASE_URL =
      process.env.NEMID_TEST_SERVER_BASE_URL ||
      defaults.NEMID_TEST_SERVER_BASE_URL;
    this.DEV_ADMIN_USER_NAME =
      process.env.DEV_ADMIN_USER_NAME || defaults.DEV_ADMIN_USER_NAME;
    this.DEV_ADMIN_PASSWORD =
      process.env.DEV_ADMIN_PASSWORD || defaults.DEV_ADMIN_PASSWORD;
    this.ENABLE_USER_NAME_PASSWORD_FOR_PATIENT_CREATION =
      process.env.ENABLE_USER_NAME_PASSWORD_FOR_PATIENT_CREATION ||
      defaults.ENABLE_USER_NAME_PASSWORD_FOR_PATIENT_CREATION;
    this.ENABLE_LANGUAGE_CHANGE =
      process.env.ENABLE_LANGUAGE_CHANGE || defaults.ENABLE_LANGUAGE_CHANGE;
    this.EVENT_LOGGING = process.env.EVENT_LOGGING || defaults.EVENT_LOGGING;
  }
}

const productionDefaults: Partial<EnvironmentVariables> = {
  ENV: "prod",
  API_URL: "https://diaprofil-hcp.zitelab.eu/api/",
  AD_URL_PATH: null,
  ENABLE_DEBUGGING: "NO",
  ENABLE_SENTRY: "YES",
  NEMID_PROD_SERVER_BASE_URL: "https://diaprofil.zitelab.eu/nemid-login/",
  NEMID_TEST_SERVER_BASE_URL: "https://diaprofil.zitelab.eu/test-nemid-login/",
  DEFAULT_LANGUAGE: "da-DK",
  QUICK_LOGIN_USER_NAME: null,
  QUICK_LOGIN_PASSWORD: null,
  DEV_ADMIN_USER_NAME: null,
  DEV_ADMIN_PASSWORD: null,
  ENABLE_USER_NAME_PASSWORD_FOR_PATIENT_CREATION: "NO",
  ENABLE_LANGUAGE_CHANGE: "NO",
  EVENT_LOGGING: "YES"
};
const developmentDefaults: Partial<EnvironmentVariables> = {
  ENV: "dev",
  API_URL: "http://localhost:8801/diaprofil-backend/",
  AD_URL_PATH: "azure-ad-sas/redirect",
  ENABLE_DEBUGGING: "YES",
  ENABLE_SENTRY: "NO",
  NEMID_PROD_SERVER_BASE_URL: "https://vbs-pro-dia.zitelab.eu/nemid-login/",
  NEMID_TEST_SERVER_BASE_URL:
    "https://vbs-pro-dia.zitelab.eu/test-nemid-login/",
  DEFAULT_LANGUAGE: "da-DK",
  QUICK_LOGIN_USER_NAME: "admin",
  QUICK_LOGIN_PASSWORD: "admin",
  DEV_ADMIN_USER_NAME: "admin",
  DEV_ADMIN_PASSWORD: "admin",
  ENABLE_USER_NAME_PASSWORD_FOR_PATIENT_CREATION: "YES",
  ENABLE_LANGUAGE_CHANGE: "YES",
  EVENT_LOGGING: "NO"
};
const testDefaults: Partial<EnvironmentVariables> = {
  ENV: "test",
  API_URL: "https://diaprofil-test.zitelab.eu/api/",
  AD_URL_PATH: null,
  ENABLE_DEBUGGING: "NO",
  ENABLE_SENTRY: "YES",
  NEMID_PROD_SERVER_BASE_URL: "https://vbs-pro-dia.zitelab.eu/nemid-login/",
  NEMID_TEST_SERVER_BASE_URL:
    "https://vbs-pro-dia.zitelab.eu/test-nemid-login/",
  DEFAULT_LANGUAGE: "da-DK",
  QUICK_LOGIN_USER_NAME: null,
  QUICK_LOGIN_PASSWORD: null,
  DEV_ADMIN_USER_NAME: null,
  DEV_ADMIN_PASSWORD: null,
  ENABLE_USER_NAME_PASSWORD_FOR_PATIENT_CREATION: "YES",
  EVENT_LOGGING: "NO"
};
const kihDefaults: Partial<EnvironmentVariables> = {
  ...testDefaults,
  AD_URL_PATH: "azure-ad-sas/redirect",
  ENV: "kih",
  ENABLE_DEBUGGING: "YES",
  API_URL: "https://diaprofil-kih-hcp-test.zitelab.eu/api/",
  ENABLE_SENTRY: "NO",
  ENABLE_LANGUAGE_CHANGE: "YES",
  EVENT_LOGGING: "NO"
};

// Note that process.env is transient and cannot be reassigned, it has to be used directly.
export function environmentFactory() {
  let envVars: EnvironmentVariables;
  switch (process.env.ENV) {
    case "prod": {
      envVars = new EnvironmentVariables(productionDefaults);
      console.log("Enviromental variables: ", envVars);
      return envVars;
    }
    case "dev": {
      envVars = new EnvironmentVariables(developmentDefaults);
      console.log("Enviromental variables: ", envVars);
      return envVars;
    }
    case "test": {
      envVars = new EnvironmentVariables(testDefaults);
      console.log("Enviromental variables: ", envVars);
      return envVars;
    }
    case "kih": {
      envVars = new EnvironmentVariables(kihDefaults);
      console.log("Enviromental variables: ", envVars);
      return envVars;
    }
    default: {
      envVars = new EnvironmentVariables(developmentDefaults);
      console.log("Enviromental variables: ", envVars);
      return envVars;
    }
  }
}

export let ENV_VARS = new InjectionToken<EnvironmentVariables>("env.vars");

@NgModule({
  providers: [
    {
      provide: ENV_VARS,
      // useFactory instead of useValue so we can easily add more logic as needed.
      useFactory: environmentFactory
    }
  ]
})
export class EnvironmentsModule {}



// WEBPACK FOOTER //
// ./src/app/environment-variables/environment-variables.module.ts